import React from 'react';
import { LanguageCode } from '../types';
import { LANGUAGES, translations } from '../i18n/translations';
import {
  X,
  Home,
  Info,
  Layers,
  Newspaper,
  Calendar,
  Sparkles,
  UserCheck,
  HeartHandshake,
  FileText,
  Image,
  PhoneCall,
  Tv,
  ShieldCheck,
  Globe,
  LogOut,
  User,
  ChevronRight,
  Lock,
  Heart,
  Megaphone,
  Ticket,
} from 'lucide-react';

interface RightDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLang: LanguageCode;
  onLanguageChange: (lang: LanguageCode) => void;
  activeTab?: string;
  currentTab?: string;
  onNavClick: (tab: string) => void;
  isAdminMode?: boolean;
  onToggleAdmin?: () => void;
  isLoggedIn?: boolean;
  onOpenAuthModal?: () => void;
  onOpenAuth?: () => void;
  onLogout?: () => void;
}

export const RightDrawer: React.FC<RightDrawerProps> = ({
  isOpen,
  onClose,
  currentLang,
  onLanguageChange,
  activeTab,
  currentTab,
  onNavClick,
  isAdminMode = false,
  onToggleAdmin,
  isLoggedIn = false,
  onOpenAuthModal,
  onOpenAuth,
  onLogout,
}) => {
  if (!isOpen) return null;

  const t = translations[currentLang];
  const currentActiveTab = activeTab || currentTab || 'home';
  const handleOpenAuth = onOpenAuthModal || onOpenAuth || (() => {});
  const handleToggleAdmin = onToggleAdmin || (() => onNavClick('admin'));
  const handleLogout = onLogout || (() => {});

  const adminDrawerLinks = [
    { id: 'dashboard', label: '1. Gösterge Paneli', icon: Home },
    { id: 'news', label: '2. Haber & Duyurular', icon: Newspaper },
    { id: 'events', label: '3. Etkinlikler', icon: Calendar },
    { id: 'activities', label: '4. Faaliyetler', icon: Layers },
    { id: 'culture', label: '5. Kültür & Dil', icon: Sparkles },
    { id: 'members', label: '6. Üye Yönetimi', icon: UserCheck },
    { id: 'donations', label: '7. Bağışlar & Rapor', icon: HeartHandshake },
    { id: 'gallery', label: '8. Galeri', icon: Image },
    { id: 'documents', label: '9. Belgeler', icon: FileText },
    { id: 'settings', label: '10. Site Yönetimi', icon: Info },
    { id: 'live', label: '11. 📺 Canlı Yayın', icon: Tv },
    { id: 'admins', label: '12. 🛡️ Yöneticiler', icon: ShieldCheck },
    { id: 'ads', label: '13. 📢 REKLAM YÖNETİMİ', icon: Megaphone },
    { id: 'tickets', label: '14. 🎫 Bilet Satış & QR Kontrol', icon: Ticket },
  ];

  const drawerLinks = isAdminMode ? adminDrawerLinks : [
    { id: 'home', label: t.home, icon: Home },
    { id: 'tickets', label: '🎫 ONLINE BİLET SATIN AL', icon: Ticket },
    { id: 'about', label: t.aboutUs, icon: Info },
    { id: 'activities', label: t.activities, icon: Layers },
    { id: 'news', label: t.news, icon: Newspaper },
    { id: 'events', label: t.events, icon: Calendar },
    { id: 'culture', label: t.culture, icon: Sparkles },
    { id: 'ads', label: t.ads || 'REKLAMLAR', icon: Megaphone },
    { id: 'membership', label: t.membership, icon: UserCheck },
    { id: 'donate', label: t.donate, icon: HeartHandshake },
    { id: 'documents', label: t.documents, icon: FileText },
    { id: 'gallery', label: t.gallery, icon: Image },
    { id: 'contact', label: t.contact, icon: PhoneCall },
    { id: 'live', label: t.liveStream, icon: Tv },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Dark Overlay backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Slide-over Glass Panel */}
      <div className="relative w-[85%] max-w-[450px] h-full glass-drawer bg-[#0B1426]/98 border-l border-[#38BDF8]/30 p-6 overflow-y-auto flex flex-col justify-between shadow-2xl animate-in slide-in-from-right duration-300 z-10">
        <div>
          {/* Header & Close Button */}
          <div className="flex items-center justify-between pb-5 border-b border-[#38BDF8]/20 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#132238] border border-[#38BDF8]/50 flex items-center justify-center text-[#38BDF8] shadow-[0_0_10px_rgba(56,189,248,0.3)]">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wide">
                  {isLoggedIn ? 'Deniz Yılmaz' : t.guestUser}
                </h3>
                <p className="text-[11px] text-[#38BDF8]">
                  {isLoggedIn ? 'KKDD-2024-0042' : t.guestSubtitle}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 text-gray-300 hover:text-[#38BDF8] hover:border-[#38BDF8] hover:shadow-[0_0_12px_rgba(56,189,248,0.5)] transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Member / Auth Card */}
          <div className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-[#132238] to-[#0B1426] border border-[#38BDF8]/40 shadow-lg flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-200 font-medium">
                {isLoggedIn ? t.memberPanel : t.guestUser}
              </p>
              <p className="text-[10px] text-[#7DD3FC] mt-0.5">
                {isLoggedIn ? t.myDues : t.guestSubtitle}
              </p>
            </div>
            <button
              onClick={() => {
                onClose();
                if (isLoggedIn) {
                  onNavClick('member-panel');
                } else {
                  handleOpenAuth();
                }
              }}
              className="px-3.5 py-1.5 rounded-xl bg-[#38BDF8] text-[#0B1426] text-xs font-bold hover:scale-105 hover:shadow-[0_0_15px_rgba(56,189,248,0.7)] transition-all cursor-pointer"
            >
              {isLoggedIn ? t.profile : t.login}
            </button>
          </div>

          {/* Red Donation Button for Members in Drawer */}
          {isLoggedIn && (
            <button
              onClick={() => {
                onClose();
                onNavClick('donate');
              }}
              className="w-full mb-6 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-500 hover:to-rose-500 text-white font-extrabold text-xs sm:text-sm shadow-[0_0_20px_rgba(239,68,68,0.8)] border border-red-400 active:scale-95 transition-all cursor-pointer animate-pulse"
            >
              <Heart className="w-4 h-4 fill-white text-white" />
              <span>❤️ BAĞIŞ YAP / DESTEK OL</span>
            </button>
          )}

          {/* Navigation Links */}
          <div className="space-y-1.5 mb-6">
            <p className="text-[11px] font-bold text-[#7DD3FC] uppercase tracking-wider mb-2 px-2 flex items-center gap-1.5">
              <span>{t.quickAccess || 'MENÜ'}</span>
            </p>
            {drawerLinks.map((link) => {
              const IconComp = link.icon;
              const isActive = currentActiveTab === link.id;

              return (
                <button
                  key={link.id}
                  onClick={() => {
                    onNavClick(link.id);
                    onClose();
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-[#38BDF8] text-[#0B1426] border border-[#A5F3FC] font-extrabold shadow-[0_0_20px_rgba(56,189,248,0.7)] scale-[1.02]'
                      : 'bg-[#132238] text-gray-200 border border-[#38BDF8]/35 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_15px_rgba(56,189,248,0.4)] active:scale-95'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <IconComp className={`w-4 h-4 ${isActive ? 'text-[#0B1426]' : 'text-[#38BDF8]'}`} />
                    <span>{link.label}</span>
                    {link.id === 'donate' && !isLoggedIn && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40 text-[9px] font-black uppercase tracking-wide flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5 text-red-400" />
                        <span>Kilitli</span>
                      </span>
                    )}
                  </div>
                  <ChevronRight className={`w-3.5 h-3.5 ${isActive ? 'text-[#0B1426]' : 'text-gray-500'}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer Settings & Language */}
        <div className="pt-4 border-t border-[#38BDF8]/20 space-y-4">
          {/* Language Switcher Grid */}
          <div>
            <p className="text-[11px] font-bold text-[#7DD3FC] uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-[#38BDF8]" />
              <span>{t.languageSelect}</span>
            </p>
            <div className="grid grid-cols-5 gap-1.5">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => onLanguageChange(lang.code)}
                  className={`py-2 text-[11px] font-bold rounded-xl border transition-all text-center cursor-pointer ${
                    currentLang === lang.code
                      ? 'bg-[#38BDF8] text-[#0B1426] border-[#A5F3FC] shadow-[0_0_12px_rgba(56,189,248,0.7)]'
                      : 'bg-[#132238] text-gray-300 border-[#38BDF8]/30 hover:border-[#38BDF8] hover:text-[#38BDF8] hover:shadow-[0_0_8px_rgba(56,189,248,0.3)]'
                  }`}
                >
                  {lang.code}
                </button>
              ))}
            </div>
          </div>

          {/* Logout / Exit */}
          {isLoggedIn && (
            <button
              onClick={() => {
                handleLogout();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-950/40 text-red-400 border border-red-800/40 hover:bg-red-900/60 transition-all text-xs font-bold"
            >
              <LogOut className="w-4 h-4" />
              <span>{t.logout}</span>
            </button>
          )}

          {/* Small Admin Button & Footer Info */}
          <div className="text-center text-[10px] text-gray-500 pt-2 space-y-2">
            <button
              onClick={() => {
                handleToggleAdmin();
                onClose();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-[#D4AF37] text-xs font-medium border border-white/10 transition-all shadow-sm"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>{isAdminMode ? 'Yönetimden Çık' : 'Yönetim Paneli Girişi'}</span>
            </button>
            <p>Kürt Kültür ve Dayanışma Derneği © 2026</p>
            <p className="text-[#D4AF37]/60">Versiyon 2.0</p>
          </div>
        </div>
      </div>
    </div>
  );
};
